import { Alert, Code, Stack } from '@mantine/core';
import isError from 'lodash-es/isError';
import { z } from 'zod';

import { Warning } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { SimpleLayout } from '@apple/ui/layouts/simple';
import { ServerError, ServerProblemError } from '@apple/utils/api/errors';
import { getEnv } from '@apple/utils/config/env';

const dev = getEnv().DEV;
const DEFAULT_ERROR_NAME = 'Error';
const DEFAULT_SERVER_ERROR_MESSAGE = 'An error has occurred.';

export type ErrorFallbackProps = {
	error: unknown;
	componentStack?: string;
	onReset?: () => void;
};

export function ErrorFallback({ error, onReset, componentStack }: ErrorFallbackProps) {
	const { t } = useTranslation('common');

	const errorName = !isError(error)
		? undefined
		: dev || (error instanceof ServerError && error.name !== DEFAULT_ERROR_NAME)
			? error.name
			: t('error.sorry');

	const errorMessage =
		isError(error) &&
		(error instanceof ServerProblemError
			? dev
				? error.problem.detail || error.message
				: t('error.generic')
			: (dev || error instanceof ServerError) &&
					error.message &&
					error.message !== DEFAULT_SERVER_ERROR_MESSAGE
				? error.message
				: t('error.generic'));

	return (
		<SimpleLayout fluid>
			<Alert
				variant='light'
				color='red'
				radius='lg'
				m='lg'
				title={errorName}
				icon={<Warning />}
				withCloseButton
				onClose={onReset}
			>
				{isError(error) && (
					<Stack>
						{errorMessage}
						{dev && error instanceof z.ZodError && (
							<>
								<Code block>{JSON.stringify(error.issues, null, 4)}</Code>
								<Code block>{JSON.stringify(z.prettifyError(error), null, 4)}</Code>
							</>
						)}
						{dev && componentStack && <Code block>{componentStack}</Code>}
						{dev && error.stack && !(error instanceof ServerProblemError) && (
							<Code block>{error.stack}</Code>
						)}
					</Stack>
				)}
			</Alert>
		</SimpleLayout>
	);
}
